import { Button } from 'primereact/button';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DropDownInput from '../../../../../../../../../../../../../../components/forms/dropdown-input/DropDownInput';
import NumberInput from '../../../../../../../../../../../../../../components/forms/number-input/NumberInput';
import {
  EMISSION_MAX_FRACTION_DIGIT,
  MAX_ENERGY_SOURCES_PER_MACHINE,
} from '../../../../../../../../../../../../../../shared/constants/products.constant';
import { ENERGY_SOURCE, IEnergySourceInput } from '../../../../../../PcfWizard.type';
import RemoveButton from '../../../../RemoveButton';
import { getEnergySourceOptions } from '../../helper/getEnergySourceOptions';

interface EnergyInputSectionProps {
  name: string;
  processIndex: number;
  machineIndex: number;
}

/**
 * Up to 4 energy input rows for an Individual Process's single MAIN machine, one per energy
 * source (PAC-1617). AC1 guarantees there is always at least one row — the dropdown already
 * disables sources used by other rows and the "Add" button disables at 4, so the schema's
 * min-1/max-4/no-duplicate rules are a defensive backstop rather than something the UI lets a
 * user actually trigger.
 */
const EnergyInputSection = ({ name, processIndex, machineIndex }: EnergyInputSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const basePath = `${name}[${processIndex}].machines[${machineIndex}].energy_sources`;

  const { fields, append, remove } = useFieldArray({ control, name: basePath });
  const energySources = (useWatch({ control, name: basePath }) ??
    []) as Partial<IEnergySourceInput>[];

  // AC1: there is always at least one row. Self-heals an empty array (e.g. a process created
  // before this field existed) instead of showing nothing but an "Add" button.
  useEffect(() => {
    if (fields.length === 0) {
      append({ source: null, amount: null });
    }
  }, [fields.length, append]);

  const usedSources = useMemo(() => {
    return energySources.map((row) => {
      return row?.source;
    });
  }, [energySources]);

  const isFull = fields.length >= MAX_ENERGY_SOURCES_PER_MACHINE;

  const handleAdd = () => {
    append({ source: null, amount: null });
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  return (
    <div className='flex flex-column'>
      <h3 className='m-0 mb-2 font-medium mb-3' style={{ fontSize: '16px' }}>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.energyInputSection.title',
        )}
      </h3>

      <div className='mb-3 font-light text-black-alpha-60'>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.energyInputSection.helperText',
        )}
      </div>

      {fields.map((field, index) => {
        return (
          <div key={field.id} className='flex align-items-center gap-3 mb-2'>
            <div className='flex-1'>
              <NumberInput
                name={`${basePath}[${index}].amount`}
                control={control}
                label={t(
                  'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.energyPerPiece',
                )}
                minFractionDigits={0}
                maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
              />
            </div>
            <div className='flex-1'>
              <DropDownInput
                name={`${basePath}[${index}].source`}
                control={control}
                label={t(
                  'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.energySource',
                )}
                options={getEnergySourceOptions(
                  t,
                  usedSources,
                  energySources[index]?.source as ENERGY_SOURCE | null,
                )}
                optionDisabled='disabled'
              />
            </div>
            {/* Fixed width regardless of whether Remove renders, so the two fields above never
                resize depending on the row. */}
            <div className='flex-shrink-0' style={{ width: '5.5rem' }}>
              {fields.length > 1 && (
                <RemoveButton
                  onClick={() => {
                    return handleRemove(index);
                  }}
                />
              )}
            </div>
          </div>
        );
      })}

      <div className='flex align-items-center gap-3'>
        <Button
          label={t(
            'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.energyInputSection.addAnotherEnergyInput',
          )}
          icon='pi pi-plus-circle'
          className='p-button-text p-button-warning py-1'
          disabled={isFull}
          onClick={handleAdd}
        />

        {isFull && (
          <span className='font-medium text-error'>
            {t(
              'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.energyInputSection.allSourcesInUseHint',
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default EnergyInputSection;
